import React, { useCallback, useEffect, useRef, useState } from 'react';
import shared from './GarageContent.module.scss';
import classes from './InquiryThread.module.scss';
import { MediaViewer } from './MediaGallery';

const formatStamp = (value) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        + ' · '
        + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

// What may go with a reply. These have to match the API
// (Backend/app/middlewares/inspectionMediaUpload.js): staff send photos and
// PDFs, customers photos only.
const MAX_FILE_MB = 25;
const MAX_FILES = { staff: 10, customer: 5 };
const PDF = 'application/pdf';
// Photos the browser can draw. Anything else (a PDF, an iPhone HEIC) is shown
// as a file to open.
const DRAWABLE = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];

// "a photo", "3 photos and a PDF" - the API's dealerCopy.attachmentsLabel.
export const attachmentsLabel = (attachments) => {
    const list = attachments || [];
    const pdfs = list.filter((a) => a.contentType === PDF).length;
    const photos = list.length - pdfs;
    const count = (n, noun) => (n === 1 ? `a ${noun}` : `${n} ${noun}s`);
    return [photos && count(photos, 'photo'), pdfs && count(pdfs, 'PDF')].filter(Boolean).join(' and ');
};

let nextFileId = 0;

/**
 * The conversation on one dealer inquiry, shared by the customer garage and
 * the Management admin queue.
 *
 * `viewer` decides which side renders as "mine" (right-aligned): the customer
 * sees their own messages on the right, staff see staff messages on the right.
 * The original form submission is rendered as the first customer bubble so the
 * thread reads as one conversation from the start.
 *
 * A reply can carry photos (staff: PDFs too) - picked with the paperclip,
 * pasted, or dropped on the reply box. onSendReply(body, files) resolves to
 * true when sent; false, or a message to show under the box, when not.
 */
const InquiryThread = ({ inquiry, viewer = 'customer', onSendReply, sending = false, replyPlaceholder }) => {
    const [draft, setDraft] = useState('');
    // Files waiting to go with the reply: { id, file, preview } - preview is
    // an object URL for a photo the browser can draw.
    const [files, setFiles] = useState([]);
    const [fileError, setFileError] = useState('');
    const [dragging, setDragging] = useState(false);
    // The photo open full size: { messageId, index }.
    const [viewing, setViewing] = useState(null);
    const pickerRef = useRef(null);

    const allowPdf = viewer === 'staff';
    const maxFiles = MAX_FILES[viewer] || MAX_FILES.customer;

    const bubbles = [];
    if (inquiry.message) {
        bubbles.push({
            _id: 'original',
            author: 'customer',
            authorName: [inquiry.firstName, inquiry.lastName].filter(Boolean).join(' '),
            body: inquiry.message,
            createdAt: inquiry.createdAt,
        });
    }
    (inquiry.messages || []).forEach((m) => bubbles.push(m));

    // The list scrolls inside its own box (max-height in the stylesheet), so
    // keep the newest message in view: on open, and when one is added here
    // or by a refresh.
    const listRef = useRef(null);
    const count = bubbles.length;
    useEffect(() => {
        const list = listRef.current;
        if (list) list.scrollTop = list.scrollHeight;
    }, [count]);

    // Object URLs live until the file leaves the list (or the thread closes).
    const filesRef = useRef(files);
    filesRef.current = files;
    useEffect(() => () => filesRef.current.forEach((f) => f.preview && URL.revokeObjectURL(f.preview)), []);

    const clearFiles = () => {
        files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
        setFiles([]);
    };

    const removeFile = (id) => {
        const gone = files.find((f) => f.id === id);
        if (gone && gone.preview) URL.revokeObjectURL(gone.preview);
        setFiles(files.filter((f) => f.id !== id));
        setFileError('');
    };

    // Takes what was picked, pasted or dropped; turns away what the API would.
    const addFiles = (picked) => {
        const incoming = Array.from(picked || []);
        if (!incoming.length) return;
        const kinds = allowPdf ? 'a photo or PDF' : 'a photo';
        const problem = (file) => {
            const type = file.type || '';
            if (!(type.startsWith('image/') || (allowPdf && type === PDF))) return `"${file.name}" isn't ${kinds}.`;
            if (file.size > MAX_FILE_MB * 1024 * 1024) return `"${file.name}" is over ${MAX_FILE_MB}MB.`;
            return '';
        };
        const errors = incoming.map(problem).filter(Boolean);
        const ok = incoming.filter((file) => !problem(file));
        const room = maxFiles - files.length;
        if (ok.length > room) errors.push(`You can send up to ${maxFiles} files at once.`);
        const added = ok.slice(0, Math.max(0, room)).map((file) => ({
            id: (nextFileId += 1),
            file,
            preview: DRAWABLE.includes(file.type) ? URL.createObjectURL(file) : '',
        }));
        if (added.length) setFiles([...files, ...added]);
        setFileError(errors.join(' '));
    };

    const submit = (e) => {
        e.preventDefault();
        const body = draft.trim();
        if ((!body && !files.length) || sending) return;
        setFileError('');
        Promise.resolve(onSendReply(body, files.map((f) => f.file))).then((ok) => {
            // Only clear the box when the send actually went through, so a
            // failed reply isn't lost.
            if (typeof ok === 'string') {
                setFileError(ok);
            } else if (ok !== false) {
                setDraft('');
                clearFiles();
            }
        });
    };

    // A screenshot pasted into the reply box is attached, not lost.
    const onPaste = (e) => {
        const pasted = e.clipboardData && e.clipboardData.files;
        if (pasted && pasted.length) {
            e.preventDefault();
            addFiles(pasted);
        }
    };

    const hasFiles = (e) => Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes('Files');
    const onDragOver = (e) => {
        if (!hasFiles(e) || sending) return;
        e.preventDefault();
        setDragging(true);
    };
    const onDrop = (e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        setDragging(false);
        if (!sending) addFiles(e.dataTransfer.files);
    };

    const viewed = viewing && bubbles.find((m) => m._id === viewing.messageId);
    const viewedPhotos = viewed ? (viewed.attachments || []).filter((a) => a.kind === 'image') : [];
    const closeViewer = useCallback(() => setViewing(null), []);
    const photoCount = viewedPhotos.length;
    const stepViewer = useCallback((delta) => {
        setViewing((v) => (v ? { ...v, index: (v.index + delta + photoCount) % photoCount } : v));
    }, [photoCount]);

    const renderAttachments = (m) => {
        const list = m.attachments || [];
        if (!list.length) return null;
        const photos = list.filter((a) => a.kind === 'image');
        const others = list.filter((a) => a.kind !== 'image');
        return (
            <div className={classes.attachments}>
                {photos.length > 0 && (
                    <ul className={classes.photos}>
                        {photos.map((a, index) => (
                            <li key={a.url}>
                                <button
                                    type="button"
                                    className={classes.photo}
                                    onClick={() => setViewing({ messageId: m._id, index })}
                                >
                                    <img src={a.url} alt={a.name ? `Photo: ${a.name}` : `Photo ${index + 1} of ${photos.length}`} loading="lazy" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                {others.map((a) => (
                    <a key={a.url} className={classes.fileLink} href={a.url} target="_blank" rel="noopener noreferrer">
                        <i className={`fas ${a.contentType === PDF ? 'fa-file-pdf' : 'fa-file-image'}`} aria-hidden="true"></i>
                        <span>{a.name || (a.contentType === PDF ? 'PDF' : 'Photo')}</span>
                    </a>
                ))}
            </div>
        );
    };

    const canSend = !!draft.trim() || files.length > 0;

    return (
        <div className={classes.thread} data-pending-files={files.length || undefined}>
            {bubbles.length === 0 && (
                <p className={classes.emptyNote}>
                    {viewer === 'staff'
                        ? 'No message was included - start the conversation below.'
                        : 'No messages yet.'}
                </p>
            )}
            <div className={classes.messages} ref={listRef}>
                {bubbles.map((m) => {
                    const mine = m.author === viewer;
                    return (
                        <div key={m._id} className={`${classes.bubbleRow} ${mine ? classes.mine : classes.theirs}`}>
                            <div className={`${classes.bubble} ${m.author === 'staff' ? classes.staffBubble : classes.customerBubble}`}>
                                <div className={classes.bubbleMeta}>
                                    <span className={classes.author}>
                                        {m.author === 'staff'
                                            ? (m.authorName || 'Import JDM Magic')
                                            : (m.authorName || 'You')}
                                    </span>
                                    <span className={classes.stamp}>
                                        {formatStamp(m.createdAt)}{m.via === 'email' ? ' · by email' : m.via === 'form' ? ' · website form' : ''}
                                    </span>
                                </div>
                                {m.body && <p className={classes.body}>{m.body}</p>}
                                {renderAttachments(m)}
                            </div>
                        </div>
                    );
                })}
            </div>

            <MediaViewer
                items={viewedPhotos.map((a) => ({ url: a.url, type: 'image' }))}
                openAt={viewing && viewing.index < photoCount ? viewing.index : null}
                onClose={closeViewer}
                onStep={stepViewer}
                label="Photos in the conversation"
                describe={(item, index) => `Photo ${index + 1} of ${photoCount}`}
            />

            {files.length > 0 && (
                <ul className={classes.pending} aria-label="Attached to your reply">
                    {files.map((f) => (
                        <li key={f.id} className={classes.pendingItem}>
                            {f.preview
                                ? <img src={f.preview} alt="" />
                                : <i className={`fas ${f.file.type === PDF ? 'fa-file-pdf' : 'fa-file-image'}`} aria-hidden="true"></i>}
                            <span className={classes.pendingName}>{f.file.name}</span>
                            <button
                                type="button"
                                className={classes.pendingRemove}
                                onClick={() => removeFile(f.id)}
                                disabled={sending}
                                aria-label={`Remove ${f.file.name}`}
                            >
                                <i className="fas fa-times" aria-hidden="true"></i>
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <form
                className={`${classes.composer} ${dragging ? classes.dragging : ''}`}
                onSubmit={submit}
                onDragOver={onDragOver}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }}
                onDrop={onDrop}
            >
                <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onPaste={onPaste}
                    maxLength={4000}
                    rows={2}
                    placeholder={replyPlaceholder
                        || (viewer === 'staff' ? 'Reply to the customer…' : 'Write a reply…')}
                    disabled={sending}
                />
                <button
                    type="button"
                    className={classes.attachButton}
                    onClick={() => pickerRef.current && pickerRef.current.click()}
                    disabled={sending || files.length >= maxFiles}
                    title={allowPdf ? 'Attach photos or PDFs' : 'Attach photos'}
                >
                    <i className="fas fa-paperclip" aria-hidden="true"></i>
                    <span className={shared.srOnly}>{allowPdf ? 'Attach photos or PDFs' : 'Attach photos'}</span>
                </button>
                <button type="submit" disabled={sending || !canSend}>
                    {sending ? 'Sending…' : 'Send'}
                </button>
                <input
                    ref={pickerRef}
                    type="file"
                    multiple
                    accept={allowPdf ? 'image/*,application/pdf' : 'image/*'}
                    className={shared.srOnly}
                    tabIndex={-1}
                    aria-hidden="true"
                    onChange={(e) => {
                        addFiles(e.target.files);
                        // The same file can be picked again after removing it.
                        e.target.value = '';
                    }}
                />
            </form>
            {fileError && <p className={classes.fileError} role="alert">{fileError}</p>}
            {viewer === 'customer' && (
                <p className={classes.composerNote}>
                    We answer here and by email. The conversation stays saved in your garage.
                </p>
            )}
        </div>
    );
};

export default InquiryThread;
